import React, { useCallback, useState } from "react"
import { useDropzone } from "react-dropzone"
import styled from "styled-components"
import { Branding, ErrorMessagesConfiguration } from "../../branding/branding"
import { useLanguageState } from "../../globalStates/LanguageState"
import { mapErrorsToString } from "../../utils/Errors"
import Button from "../Button"
import LoadingIndicator from "../LoadingIndicator"
import DummyPreviewIcon from '../../images/dummy.svg'
import PdfPreviewIcon from '../../images/pdf.svg'

function mapSource(source?: string, fileType?: string): string {
    if (!source) return 'none'
    if (fileType) {
        switch (fileType) {
            case 'application/pdf': return PdfPreviewIcon
            case 'image/jpeg': return source
        }
        return DummyPreviewIcon
    }
    return source
}

//TODO - make it generic
function translateErrors(error: string, branding: Branding): string {
    const messageKey = error || ''
    const errorMessage = branding.configuration.errorMessages[messageKey as keyof ErrorMessagesConfiguration]
    return errorMessage || messageKey
}

const FilePickerWrapper = styled.div`
    display: flex;
    flex-direction: column;
    & > label:first-child {
        font-weight: 700;
    }
    & > label {
        margin-bottom: 25px;
    }
`

const Message = styled.label`
    font-size: 12px;
`

interface StyledDropzoneProps {
    source?: string
    fileType?: string
    rounded?: boolean
}

const StyledDropzone = styled.div<StyledDropzoneProps>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    border-radius: ${(props) => (props.rounded ? "50%" : "5px")};
    background-size: contain;
    background-image: url(${props => mapSource(props.source, props.fileType)});
    background-repeat: no-repeat;
    background-position: center;
    margin-bottom: 20px;
    border: ${(props) => (props.source ? "none" : "2px dashed #c9c9c9")};
    min-height: 200px;
    min-width: 200px;
    max-height: 200px;
    &.invalid {
        background: rgba(255, 0, 0, 0.1);
        border: 2px dashed #ff0000;
    }
`

const ButtonsBar = styled.div`
    margin-left: -5px;
    margin-right: -5px;
    & > * {
        width: calc(50% - 10px);
        min-width: 200px;
        margin: 5px;
    }
`

const UploadMessage = styled.label`
    text-align: center;
    color: #ff0000;
`

const PlaceholderTitle = styled.div`
    font-weight: 500;
    font-size: 16px;
    line-height: 19px;
    text-align: center;
    color: #727272;
    margin-bottom: 7.5px;
`

const PlaceholderText = styled.div`
    font-weight: normal;
    font-size: 12px;
    line-height: 18px;
    text-align: center;
    color: #727272;
    margin-top: 7.5px;
    padding: 0 40px;
`

interface FilePickerProps {
    name: string
    label: string
    message?: string
    placeholderTitle: string
    placeholderText: string
    value?: string
    fileType?: string
    readonly?: boolean
    rounded?: boolean
    acceptFilter?: string
    browseLabel: string
    deleteLabel: string
    onChangeImage: (
        image: File,
        onUploadProgress: (progress: number) => void,
        onSuccessCallback: () => void,
        onErrorCallback: (reason: string) => void
    ) => void
    onRemoveImage: () => void
}

const FilePicker: React.FC<FilePickerProps> = ({
    name,
    label,
    message,
    placeholderTitle,
    placeholderText,
    value,
    fileType,
    readonly,
    rounded,
    acceptFilter = "image/jpeg, image/png",
    browseLabel,
    deleteLabel,
    onChangeImage,
    onRemoveImage,
}) => {
    const branding = useLanguageState().getStrings()
    const [uploading, setUploading] = useState(false)
    const [error, setError] = useState<string | undefined>(undefined)
    const [progress, setProgress] = useState(0)

    const onUploadProgress = useCallback((progress: number) => {
        setProgress(progress)
    }, [])
    const onSuccessCallback = useCallback(() => {
        setUploading(false)
        setError(undefined)
    }, [])
    const onErrorCallback = useCallback((reason: string) => {
        setUploading(false)
        setError(mapErrorsToString(reason))
    }, [])
    const onDrop = useCallback(
        (images: File[]) => {
            setError(undefined)
            setUploading(true)
            const image = images && images[0]
            if (image) {
                onChangeImage(image, onUploadProgress, onSuccessCallback, onErrorCallback)
            } else {
                onErrorCallback('invalidFileType')
            }
        },
        [onChangeImage, onErrorCallback, onSuccessCallback, onUploadProgress]
    )

    const onRemove = useCallback(() => {
        setUploading(false)
        setError(undefined)
        onRemoveImage()
    }, [onRemoveImage])

    const { getRootProps, getInputProps, open } = useDropzone({ onDrop, accept: acceptFilter, multiple: false })
    const rootProps = readonly ? {} : { ...getRootProps({ refKey: "innerRef" }) }
    return (
        <FilePickerWrapper>
            <label>{label}</label>
            {message && <Message>{message}</Message>}
            <StyledDropzone
                rounded={rounded}
                {...rootProps}
                id={name}
                className={error ? "invalid" : ""}
                style={{ position: "relative" }}
                source={value}
                fileType={fileType}
            >
                {!readonly && <input {...getInputProps()} />}
                {error && <UploadMessage>{translateErrors(error, branding)}</UploadMessage>}
                {!value && !error && <PlaceholderTitle>{placeholderTitle}</PlaceholderTitle>}
                {!value && <PlaceholderText>{placeholderText}</PlaceholderText>}
                {uploading && <LoadingIndicator progress={progress} />}
            </StyledDropzone>
            {!readonly && (
                <ButtonsBar>
                    <Button
                        caption={browseLabel}
                        inverse
                        onClick={open}
                    />
                    <Button
                        caption={deleteLabel}
                        inverse={false}
                        onClick={onRemove}
                    />
                </ButtonsBar>
            )}
        </FilePickerWrapper>
    )
}
export default FilePicker
